import { Instagram, Linkedin, Mail, Phone } from "lucide-react";
import { handleScrollTo } from "../App";

const QUICK_LINKS = [
  { label: "Home", href: "#hero" },
  { label: "Products", href: "#products" },
  { label: "Manufacturing & Capabilities", href: "#capabilities" },
  { label: "Certifications", href: "#certificates" },
  { label: "About Us", href: "#about" },
  { label: "Contact Us", href: "#contact" },
  { label: "Download Catalogue", href: "#download" },
  { label: "Request a Quote", href: "#hero" },
];

const PRODUCT_CATEGORIES = [
  "Signal, Stop & Indicator Lamps",
  "Wedge & Miniature Lamps",
  "Headlamp & Forward Lighting",
  "Interior & Auxiliary Lamps",
  "Commercial Vehicle & 24V Lamps",
  "Defence & Specialised Lamps",
];

const COMPLIANCE_POINTS = [
  "ISO / IATF Certified Manufacturing",
  "Two Manufacturing Facilities",
  "Automated Production Lines",
  "OEM & Program-Based Supply",
  "Batch Traceability & Quality Control",
];

const FooterSection = ({ isDesktop, THEME }) => (
  <footer
    style={{
      backgroundColor: THEME.colors.bg,
      color: "white",
      padding: "80px 0 40px",
      borderTop: "1px solid #27272a",
    }}
  >
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "0 24px",
        display: "grid",
        gridTemplateColumns: isDesktop ? "1.6fr 1fr 1fr 1fr 1.2fr" : "1fr",
        gap: "48px",
      }}
    >
      {/* COLUMN 1 — COMPANY */}
      <div>
        <img
          src="/logo.png"
          alt="FOKAL Lamps"
          style={{
            height: "32px",
            width: "auto",
            objectFit: "contain",
            marginBottom: "24px",
          }}
        />

        <p
          style={{
            color: THEME.colors.textMuted,
            fontSize: "14px",
            lineHeight: "1.6",
            marginBottom: "16px",
            maxWidth: "340px",
          }}
        >
          OEM & defence-grade automotive lighting manufacturer with over four
          decades of experience in precision-engineered incandescent and halogen
          lamp production.
        </p>

        <p
          style={{
            color: THEME.colors.textMuted,
            fontSize: "14px",
            lineHeight: "1.6",
            maxWidth: "340px",
          }}
        >
          Serving regulated automotive, commercial vehicle, defence, and
          infrastructure applications through certified manufacturing and
          long-term OEM programs.
        </p>

        <div style={{ display: "flex", gap: "16px", marginTop: "24px" }}>
          <div
            style={{
              padding: "10px",
              border: "1px solid #3f3f46",
              borderRadius: "50%",
              cursor: "pointer",
            }}
          >
            <Linkedin size={18} />
          </div>
          <div
            style={{
              padding: "10px",
              border: "1px solid #3f3f46",
              borderRadius: "50%",
              cursor: "pointer",
            }}
          >
            <Instagram size={18} />
          </div>
        </div>
      </div>

      {/* COLUMN 2 — QUICK LINKS */}
      <div>
        <FooterHeading title="Quick Links" />
        <FooterList>
          {QUICK_LINKS.map((link) => (
            <FooterLink key={link.label} label={link.label} href={link.href} />
          ))}
        </FooterList>
      </div>

      {/* COLUMN 3 — PRODUCT CATEGORIES */}
      <div>
        <FooterHeading title="Product Categories" />
        <FooterList>
          {PRODUCT_CATEGORIES.map((item) => (
            <li
              key={item}
              style={{ fontSize: "14px", color: THEME.colors.textMuted }}
            >
              <a
                href="#products"
                onClick={(e) => handleScrollTo(e, "#products")}
                style={{
                  textDecoration: "none",
                  color: THEME.colors.textMuted,
                }}
              >
                {item}
              </a>
            </li>
          ))}
        </FooterList>
      </div>

      {/* COLUMN 4 — MANUFACTURING & COMPLIANCE */}
      <div>
        <FooterHeading title="Manufacturing & Compliance" />
        <FooterList>
          {COMPLIANCE_POINTS.map((item) => (
            <li
              key={item}
              style={{ fontSize: "14px", color: THEME.colors.textMuted }}
            >
              {item}
            </li>
          ))}
        </FooterList>
      </div>

      {/* COLUMN 5 — CONTACT */}
      <div>
        <FooterHeading title="Contact" />

        <p
          style={{
            color: THEME.colors.textMuted,
            fontSize: "14px",
            lineHeight: "1.6",
            marginBottom: "16px",
          }}
        >
          <strong style={{ color: "white" }}>Manufacturing Facilities</strong>
          <br />
          Faridabad & Palwal, Haryana, India
        </p>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            fontSize: "14px",
            color: THEME.colors.textMuted,
          }}
        >
          <div style={{ display: "flex", gap: "12px" }}>
            <Phone size={16} color={THEME.colors.accent} />
            <span>+91 81303 29152</span>
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <Mail size={16} color={THEME.colors.accent} />
            <span>custcare@fokallamps.com</span>
          </div>
        </div>
      </div>
    </div>

    {/* FOOTER BOTTOM BAR */}
    <div
      style={{
        maxWidth: "1400px",
        margin: "60px auto 0",
        padding: "32px 24px 0",
        borderTop: "1px solid #27272a",
        color: "#52525b",
        fontSize: "12px",
      }}
    >
      <div style={{ marginBottom: "8px" }}>
        © 2026 FOKAL. All rights reserved.
      </div>
      <div style={{ maxWidth: "720px", lineHeight: "1.6" }}>
        Products supplied to OEM partners are integrated into vehicles and
        systems operating across multiple global markets.
      </div>
    </div>
  </footer>
);

/* ----------------- SMALL HELPERS ----------------- */

const FooterHeading = ({ title }) => (
  <h4
    style={{
      fontSize: "14px",
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      marginBottom: "24px",
      color: "white",
    }}
  >
    {title}
  </h4>
);

const FooterList = ({ children }) => (
  <ul
    style={{
      listStyle: "none",
      padding: 0,
      margin: 0,
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    }}
  >
    {children}
  </ul>
);

const FooterLink = ({ label, href }) => (
  <li>
    <a
      href={href}
      onClick={(e) => handleScrollTo(e, href)}
      style={{
        textDecoration: "none",
        color: "#a1a1aa",
        fontSize: "14px",
      }}
    >
      {label}
    </a>
  </li>
);

export default FooterSection;
